/**
 * Patient Import API
 *
 * Enhances codegen'd data-migration endpoints with cache tags
 * and adds a custom file upload mutation (codegen can't handle FormData).
 */

import type { MigrationSessionCreateResponse } from '@store/generated/data-migration';
import { enhancedApi as dataMigrationApi } from '@store/generated/data-migration';

// -----------------------------------------------------------------------------
// Enhance generated endpoints with cache tag behaviour
// -----------------------------------------------------------------------------

export const patientImportApi = dataMigrationApi.enhanceEndpoints({
  addTagTypes: [
    'PatientImportSession',
    'PatientImportSessionList',
  ],
  endpoints: {
    providerListImportSessions: {
      providesTags: (result) =>
        result
          ? [
              ...result.sessions.map((s) => ({
                type: 'PatientImportSessionList' as const,
                id: s.session_id,
              })),
              { type: 'PatientImportSessionList' as const, id: 'LIST' },
            ]
          : [{ type: 'PatientImportSessionList' as const, id: 'LIST' }],
    },
    approveCsvImport: {
      invalidatesTags: (_result, _error, arg) => [
        { type: 'PatientImportSession' as const, id: arg.sessionId },
        { type: 'PatientImportSessionList' as const, id: 'LIST' },
      ],
    },
    providerDeleteImportSession: {
      invalidatesTags: [
        { type: 'PatientImportSessionList' as const, id: 'LIST' },
      ],
    },
  },
});

// -----------------------------------------------------------------------------
// Custom upload mutation — codegen only emits { organization_id } as the body,
// but the real endpoint expects multipart/form-data with the file attached.
// -----------------------------------------------------------------------------

const uploadApi = patientImportApi.injectEndpoints({
  endpoints: (builder) => ({
    uploadPatientImportFile: builder.mutation<
      MigrationSessionCreateResponse,
      { file: File; organizationId: string }
    >({
      query: ({ file, organizationId }) => {
        const formData = new FormData();
        formData.append('file', file, file.name);
        formData.append('organization_id', organizationId);
        return {
          url: '/v2/provider/data-migration/upload',
          method: 'POST',
          body: formData,
        };
      },
      invalidatesTags: [
        { type: 'PatientImportSessionList' as const, id: 'LIST' },
      ],
    }),
  }),
  overrideExisting: false,
});

export const { useUploadPatientImportFileMutation } = uploadApi;
